import * as z from "zod";
import { yyyyMmDdSchema } from "../common/zod-util";

export const recoverConversationGroupKeys = [
  "marketingConversationId",
  "automation",
  "automationStepId",
  "treatmentId",
] as const;

export type RecoverConversationGroupKey = Partial<
  Record<(typeof recoverConversationGroupKeys)[number], string | null>
>;

export const RecoverConversationGroupKeySchema = z
  .object({
    marketingConversationId: z.string().nullish(),
    automation: z.string().nullish(),
    automationStepId: z.string().nullish(),
    treatmentId: z.string().nullish(),
  })
  .partial();

export const validRecoverConversationGroupings: Set<
  keyof RecoverConversationGroupKey
>[] = [
  new Set([]),
  new Set(["marketingConversationId"]),
  new Set(["automation"]),
  new Set(["automation", "automationStepId"]),
  // A/B testing groupings
  new Set(["treatmentId"]),
  new Set(["automation", "treatmentId"]),
  new Set(["automation", "automationStepId", "treatmentId"]),
];

export const recoverConversationMetrics = [
  "initialSends",
  "replySends",
  "customerReplies",
  "uniqueCustomerReplies",
  "recoveredOrders",
  "recoveredRevenue",
  "unsubscribes",
  "skippedDueToUnsubscribe",
  "skippedDueToSkipConditions",
  "checkoutAbandonments",
  "controlSuppressed",
] as const;

export type RecoverConversationMetric =
  (typeof recoverConversationMetrics)[number];

export type DailyMetricKey =
  | RecoverConversationMetric
  | "totalSends"
  | "replyRate";

export const RecoverMetricDataPointSchema = z.object({
  date: yyyyMmDdSchema.describe("Date for this data point (YYYY-MM-DD)"),
  value: z.number().describe("Value for this metric on this date"),
});
export type RecoverMetricDataPoint = z.infer<
  typeof RecoverMetricDataPointSchema
>;

export const RecoverMetricTimeSeriesSchema = z.object({
  metricName: z.enum(recoverConversationMetrics),
  data: z.array(RecoverMetricDataPointSchema),
});
export type RecoverMetricTimeSeries = z.infer<
  typeof RecoverMetricTimeSeriesSchema
>;

export const RecoverMetricTotalsSchema = z.record(
  z.enum(recoverConversationMetrics),
  z.number(),
);
export type RecoverMetricTotals = z.infer<typeof RecoverMetricTotalsSchema>;

export const RECOVER_METRIC_LABELS: Record<DailyMetricKey, string> = {
  initialSends: "Initial sends",
  replySends: "Reply sends",
  totalSends: "Total sends",
  customerReplies: "Customer replies",
  uniqueCustomerReplies: "Unique customer replies",
  replyRate: "Reply rate (%)",
  recoveredOrders: "Recovered orders",
  recoveredRevenue: "Recovered revenue",
  unsubscribes: "Unsubscribes",
  skippedDueToUnsubscribe: "Skipped (unsubscribed)",
  skippedDueToSkipConditions: "Skipped (conditions)",
  checkoutAbandonments: "Checkout abandonments",
  controlSuppressed: "Control group suppressed",
};

/** Metrics shown on the merchant recover analytics page (excludes admin-only metrics). */
export const MERCHANT_RECOVER_METRICS: DailyMetricKey[] = [
  "initialSends",
  "replySends",
  "totalSends",
  "customerReplies",
  "uniqueCustomerReplies",
  "recoveredOrders",
  "recoveredRevenue",
  "unsubscribes",
  "skippedDueToUnsubscribe",
  "skippedDueToSkipConditions",
];

export const MERCHANT_DEFAULT_VISIBLE_METRICS: DailyMetricKey[] = [
  "recoveredRevenue",
];
